<template>
  <v-menu
    v-model="showCalendar"
    :close-on-content-click="false"
    transition="scale-transition"
    offset-y
    :nudge-left="right"
    :origin="right ? 'top right' : 'top left'"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-text-field
        ref="textField"
        class="date-picker__text-field"
        :value="dateFormatted"
        filled
        flat
        solo
        append-icon="mdi-calendar"
        readonly
        v-bind="attrs"
        v-on="on"
        hide-details
        clearable
        @click:clear="$emit('input', '')"
      ></v-text-field>
    </template>
    <v-date-picker
      v-model="date"
      v-bind="computedDatePickerAttributes"
      :first-day-of-week="1"
      @input="closeCalendar"
      :locale="currentLanguage"
      :picker-date.sync="pickerDate"
      @click:date="handleClick"
      @mouseleave:date="showTooltipMessage = false"
    >
    </v-date-picker>
    <v-tooltip
      bottom
      v-model="showTooltipMessage"
      :position-x="tooltipX"
      :position-y="tooltipY"
      :max-width="200"
    >
      {{ disableDateMessage }}
    </v-tooltip>
  </v-menu>
</template>

<script>
import moment from 'moment';

/**
 * Component AlertsDatePicker.vue
 *
 * @module AlertsDatePicker
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.1] app/src/APP_DASHBOARD/templates/components/Alerts/AlertsDatePicker.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'AlertsDatePicker',
  props: {
    value: {
      type: String,
      required: true,
    },
    alignRight: {
      type: Boolean,
    },
    disableDateMessage: {
      type: String,
      default: () => ''
    },
    datePickerAttr: {},
    messageMinDate: {
      type: String,
    },
    messageMaxDate: {
      type: String,
    },
  },
  data() {
    return {
      showCalendar: false,
      right: 0,
      pickerDate: null,
      showTooltipMessage: false,
      tooltipX: 0,
      tooltipY: 0
    };
  },
  watch: {
    showCalendar(value) {
      const inputWidth = this.$refs.textField.$el.clientWidth
      if (inputWidth && this.alignRight) {
        this.right = 290 - inputWidth
      }
      if (value) {
        this.onMonthChange()
      }
    },
    pickerDate() {
      this.onMonthChange()
    },
  },
  computed: {
    date: {
      get() {
        return this.value;
      },
      set(date) {
        if (this.isValidDate(date)) {
          this.$emit('input', date);
        }
      },
    },
    dateFormatted() {
      if (!this.value) return '';
      return moment(this.value).format('DD.MM.YYYY');
    },
    currentLanguage() {
      return this.$store.getters.getCurrentLanguage
    },
    showDisableDateMessage() {
      return !!this.disableDateMessage
    },
    computedDatePickerAttributes() {
      if (!this.datePickerAttr) return;

      const {min, max} = this.datePickerAttr;
      if (!this.showDisableDateMessage || (!min && !max)) {
        return this.datePickerAttr;
      }
      // set min and max values as start and end of month, to enable all dates in the month
      const minStart = min ? moment(min).startOf('month').format('YYYY-MM-DD') : null;
      const maxEnd = max ? moment(max).endOf('month').format('YYYY-MM-DD') : null;

      if (min && max) {
        return { ...this.datePickerAttr, min: minStart, max: maxEnd }
      } else if (min) {
        return { ...this.datePickerAttr, min: minStart }
      } else {
        return { ...this.datePickerAttr, max: maxEnd }
      }
    }
  },
  methods: {
    closeCalendar(date) {
      if (this.isValidDate(date)) {
        this.showCalendar = false
      }
    },

    handleClick(date, event) {
      // show tooltip when clicking on invalid dates
      if (!this.isValidDate(date, true)) {
        // get element coordinates
        const rect = event.srcElement.getBoundingClientRect();
        this.tooltipX = rect.left + rect.width / 2;
        this.tooltipY = rect.bottom + 10;
        this.showTooltipMessage = true
      }
    },

    onMonthChange() {
      if (!this.showDisableDateMessage || !this.datePickerAttr) return;
      const {min, max} = this.datePickerAttr

      // add class to show every out of range date as disabled
      // set timeout to allow DOM to update after month has changed
      setTimeout(() => {
        const elements = document.querySelectorAll('.menuable__content__active .v-date-picker-table .v-btn.v-btn--rounded');
        const nodes = [...elements]

        nodes.forEach(item => {
          const date = '-' + ('0' + item?.innerText).slice(-2);
          const month = this.pickerDate
          let invalidDate = false;

          const minAfter = min ? moment(min).isAfter(moment(month + date), 'days') : null
          const maxBefore = max ? moment(max).isBefore(moment(month + date), 'days') : null

          if (min && max) {
            invalidDate = minAfter || maxBefore
          } else if (min) {
            invalidDate = minAfter
          } else if (max) {
            invalidDate = maxBefore
          }

          const hasClass = [...item.classList].includes('date-picker__disabled-date');

          if (!invalidDate && hasClass) {
            item.classList.remove("date-picker__disabled-date")
          }

          if (invalidDate && !hasClass) {
            item.classList.add("date-picker__disabled-date");
          }
        });
      }, 50)
    },

    isValidDate(date, messageRange) {
      if (!this.showDisableDateMessage || !this.datePickerAttr) {
        return true
      }

      let {min, max} = this.datePickerAttr
      if (messageRange && (this.messageMinDate || this.messageMaxDate)) {
        min = this.messageMinDate
        max = this.messageMaxDate
      }

      const maxAfter = max ? moment(max).isSameOrAfter(moment(date)) : null;
      const minBefore = min ? moment(min).isSameOrBefore(moment(date)) : null;
      let isValid = true;

      if (min && max) {
        isValid = maxAfter && minBefore
      } else if (min) {
        isValid = minBefore
      } else if (max) {
        isValid = maxAfter
      }

      return isValid
    }
  }
};
</script>

<style lang="scss">
.date-picker__text-field {
  max-width: 205px;
}

.date-picker__disabled-date {
  color: rgba(0,0,0,.26) !important;
  cursor: default !important;
}
</style>
